import { Link } from 'react-router-dom';
import type { NewsArticle } from '@/types/media';
import SectionHeading from '@/components/base/SectionHeading';

interface NewsHighlightsProps {
  news: NewsArticle[];
}

export default function NewsHighlights({ news }: NewsHighlightsProps) {
  if (news.length === 0) {
    return (
      <section className="max-w-8xl mx-auto px-4 md:px-8 pb-12 md:pb-16">
        <SectionHeading eyebrow="NEWS" title="热门影视资讯" desc="行业动态与影视热点，一网打尽。" moreTo="/news" moreLabel="全部资讯" />
        <div className="flex flex-col items-center justify-center py-16 text-center bg-background-100 rounded-lg border border-background-200">
          <i className="ri-newspaper-line text-4xl text-foreground-300"></i>
          <p className="mt-4 text-sm text-foreground-500">暂无影视资讯</p>
        </div>
      </section>
    );
  }

  const sorted = [...news].sort((a, b) => b.heat - a.heat);
  const featured = sorted[0];
  const rest = sorted.slice(1, 5);

  return (
    <section className="bg-background-100 border-y border-background-200">
      <div className="max-w-8xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <SectionHeading
          eyebrow="NEWS & TRENDS"
          title="热门影视资讯"
          desc="聚焦行业动态、影片口碑与幕后故事，掌握第一手影视资讯。"
          moreTo="/news"
          moreLabel="全部资讯"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Link
            to={`/news/${featured.id}`}
            className="group relative rounded-lg overflow-hidden cursor-pointer min-h-[280px] md:min-h-[340px] flex"
          >
            <img
              src={featured.cover}
              alt={featured.title}
              title={featured.title}
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/50 to-transparent"></div>
            <div className="relative z-10 mt-auto p-6 md:p-8">
              <span className="inline-block px-2.5 py-1 rounded-md bg-accent-500 text-primary-950 text-[11px] font-bold font-label">
                {featured.tag}
              </span>
              <h3 className="mt-3 font-heading text-xl md:text-2xl font-bold text-background-50 leading-snug group-hover:text-accent-400 transition-colors">
                {featured.title}
              </h3>
              <p className="mt-2 text-sm text-background-200 line-clamp-2">{featured.summary}</p>
              <p className="mt-3 text-xs text-background-300 flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                {featured.date}
              </p>
            </div>
          </Link>

          <div className="flex flex-col gap-3">
            {rest.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-28 h-20 md:w-32 md:h-[88px] rounded-md overflow-hidden bg-background-200 shrink-0">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={item.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex flex-col justify-center">
                  <span className="text-[11px] font-bold text-accent-600 font-label">{item.tag}</span>
                  <h4 className="mt-1 text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </h4>
                  <p className="mt-1.5 text-xs text-foreground-400 flex items-center gap-2">
                    <i className="ri-time-line"></i>
                    {item.date}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}